import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { CreditCard } from '../CreditCard';
import { CreditCardRewardsType } from '../CreditCardRewardsTypes';
import { CreditCardData } from '../CreditCardData';

export const usePointValues = (
  cards: CreditCard[],
  enableOverrides: boolean = false,
  setOverrides: (
    overrides:
      | Record<string, number>
      | ((prev: Record<string, number>) => Record<string, number>)
  ) => void,
  overrides: Record<string, number>
): {
  pointValues: Record<string, number>;
  setPointValues: (rewardsType: CreditCardRewardsType, value: string) => void;
  updateOverrides: (rewardsType: CreditCardRewardsType, value: string) => void;
  overrideKeys: string[];
  pointValuesForCards: Record<string, number>;
  resetPointValues: (_?: CreditCardRewardsType) => void;
} => {
  const rewardsTypes: CreditCardRewardsType[] = useMemo(() => {
    const missingCards = cards.filter((card) => !CreditCardData[card]);
    if (missingCards.length > 0) {
      console.warn('Cards not found in CreditCardData:', missingCards);
    }
    const result = _.uniq(
      cards
        .filter((card) => CreditCardData[card]) // Filter out cards that don't exist in CreditCardData
        .map((card) => CreditCardData[card].rewardsType)
    );
    console.warn('rewardsTypes', result);
    return result;
  }, [cards]);

  const pointValues: Record<CreditCardRewardsType, number> = useMemo(() => {
    // Get any rewards types that are not necessarily part of CreditCardRewardsType
    const otherRewardsTypes = cards
      .filter((card) => CreditCardData[card]) // Filter out cards that don't exist in CreditCardData
      .map((card) => CreditCardData[card].rewardsType)
      .filter(
        (rewardsType) =>
          !Object.values(CreditCardRewardsType).includes(rewardsType)
      );
    if (otherRewardsTypes.length > 0) {
      console.warn('otherRewardsTypes', otherRewardsTypes);
    }
    return {
      // Cash back
      [CreditCardRewardsType.CASH_BACK]: 1.0,
      [CreditCardRewardsType.GIFT_CARD]: 1.0,

      // Transferable points
      [CreditCardRewardsType.CHASE_ULTIMATE_REWARDS]:
        cards.includes(CreditCard.CHASE_SAPPHIRE_RESERVE_CREDIT_CARD) ||
        cards.includes(CreditCard.CHASE_SAPPHIRE_PREFERRED_CREDIT_CARD) ||
        cards.includes(CreditCard.INK_BUSINESS_PREFERRED_CREDIT_CARD)
          ? 1.85
          : 1,
      [CreditCardRewardsType.AMERICAN_EXPRESS_MEMBERSHIP_REWARDS]: 1.85,
      // Citi ThankYou points are worth 1 cpp unless you have a Strata card with transfer partners
      [CreditCardRewardsType.CITI_THANKYOU_POINTS]:
        cards.includes(CreditCard.CITI_STRATA_PREMIER_CARD) ||
        cards.includes(CreditCard.CITI_STRATA_ELITE_CARD)
          ? 1.6
          : 1,
      [CreditCardRewardsType.CAPITAL_ONE_REWARDS]: 1.6,
      [CreditCardRewardsType.BILT_REWARDS]: 1.85,
      [CreditCardRewardsType.AVIOS]: 1.5,

      // Bank points
      [CreditCardRewardsType.WELLS_FARGO_REWARDS]: 1.5,
      [CreditCardRewardsType.BANK_OF_AMERICA_REWARDS]: 1.0,
      [CreditCardRewardsType.BARCLAYCARD_REWARDS]: 1.0,
      [CreditCardRewardsType.RCI_REWARDS]: 1.0,
      [CreditCardRewardsType.BREX]: 1.0,
      [CreditCardRewardsType.US_BANK_REWARDS]: 1.0,
      [CreditCardRewardsType.PENFED]: 0.85,
      [CreditCardRewardsType.NAVY_FEDERAL_REWARDS]: 1.0,
      [CreditCardRewardsType.HSBC_REWARDS]: 0.5,

      // Hotel points
      [CreditCardRewardsType.MARRIOTT_BONVOY]: 0.85,
      [CreditCardRewardsType.HILTON]: 0.4,
      [CreditCardRewardsType.HYATT]: 1.6,
      [CreditCardRewardsType.IHG]: 0.5,
      [CreditCardRewardsType.WYNDHAM]: 1.1,
      [CreditCardRewardsType.CHOICE]: 0.6,
      [CreditCardRewardsType.CHOICE_PRIVILEGES]: 0.6,
      [CreditCardRewardsType.BEST_WESTERN]: 0.6,
      [CreditCardRewardsType.RADISSON]: 0.4,

      // Airline miles - major US airlines
      [CreditCardRewardsType.UNITED]: 1.35,
      [CreditCardRewardsType.AMERICAN]: 1.6,
      [CreditCardRewardsType.DELTA]: 1.5,
      [CreditCardRewardsType.SOUTHWEST]: 1.4,
      [CreditCardRewardsType.ALASKA]: 1.4,
      [CreditCardRewardsType.JETBLUE]: 1.3,
      [CreditCardRewardsType.HAWAIIAN]: 1.2,
      [CreditCardRewardsType.FRONTIER]: 1.1,
      [CreditCardRewardsType.SPIRIT_AIRLINES]: 1.0,

      // Status qualifying points (not redeemable - used for elite status only)
      [CreditCardRewardsType.STATUS_QUALIFYING_POINTS_UNITED]: 0,
      [CreditCardRewardsType.STATUS_QUALIFYING_POINTS_DELTA]: 0,

      // International airline miles
      [CreditCardRewardsType.AIR_FRANCE_KLM_FLYING_BLUE]: 1.3,
      [CreditCardRewardsType.AIR_CANADA_AEROPLAN]: 1.4,
      [CreditCardRewardsType.LUFTHANSA]: 1.2,
      [CreditCardRewardsType.EMIRATES]: 1.3,
      [CreditCardRewardsType.ALL_NIPPON_AIRWAYS]: 1.3,
      [CreditCardRewardsType.AVIANCA]: 1.3,
      [CreditCardRewardsType.CATHAY_PACIFIC]: 1.2,
      [CreditCardRewardsType.KOREAN]: 1.3,
      [CreditCardRewardsType.LATAM]: 1.1,
      [CreditCardRewardsType.VIRGIN]: 1.3,

      // Cruise points
      [CreditCardRewardsType.CARNIVAL]: 0.8,
      [CreditCardRewardsType.NORWEGIAN_CRUISE_POINTS]: 0.8,
      [CreditCardRewardsType.MYCRUISE_POINTS]: 0.8,
      [CreditCardRewardsType.FUNPOINTS]: 1.0,

      // Other
      [CreditCardRewardsType.DISNEY_REWARDS_DOLLARS]: 1.0,
      [CreditCardRewardsType.BREEZEPOINTS]: 1.0,
      [CreditCardRewardsType.ALLWAYS_POINTS]: 1.1,
      [CreditCardRewardsType.EXPEDIA]: 1.0,
      [CreditCardRewardsType.GM_REWARDS_POINTS]: 1.0,
      [CreditCardRewardsType.GM_BUSINESS_CARD_POINTS]: 1.0,
      [CreditCardRewardsType.GAME_PASS_ULTIMATE]: 1.0,
      [CreditCardRewardsType.AMTRAK]: 2.6,

      ...(enableOverrides ? overrides : {}),
    };
  }, [overrides, enableOverrides, cards]);

  const updateOverrides = useCallback(
    (rewardsType: CreditCardRewardsType, value: string) => {
      if (Number(value) !== pointValues[rewardsType]) {
        setOverrides((prevPointValues: Record<string, number>) => {
          return {
            ...prevPointValues,
            [rewardsType]: Number(value),
          };
        });
      }
    },
    [setOverrides, pointValues]
  );

  const resetPointValues = useCallback(
    (key?: CreditCardRewardsType) => {
      if (key) {
        setOverrides((prevPointValues: Record<string, number>) => {
          const { [key]: _, ...rest } = prevPointValues;
          return rest;
        });
      } else {
        setOverrides({});
      }
    },
    [setOverrides]
  );

  return {
    pointValues,
    pointValuesForCards: useMemo(
      () => _.pick(pointValues, rewardsTypes),
      [pointValues, rewardsTypes]
    ),
    setPointValues: updateOverrides,
    updateOverrides,
    overrideKeys: Object.keys(overrides),
    resetPointValues,
  };
};
